/**
 * accessibleAccent — derives WCAG 2.2 AA-safe accent shades from a preset's brand hex.
 *
 * A single hex cannot serve both roles: a *fill* under white text must be dark enough,
 * while *text* on the page surface must contrast with that surface — in dark mode those
 * pull in opposite directions. So each preset yields two shades:
 *
 *   fill      — button/badge background paired with white text (>= 4.5:1 vs white)
 *   onSurface — link/label/active-item text (>= 4.5:1 vs the surface AND vs the 8%
 *               accent tint the active states paint underneath it)
 *
 * The hue is preserved; only lightness moves, and only as far as the threshold requires,
 * so each preset stays recognisable. See docs/redesign_plan.md §6.B.9.
 */

const parse = (hex) => {
  const s = hex.replace("#", "");
  const full =
    s.length === 3
      ? s
          .split("")
          .map((c) => c + c)
          .join("")
      : s;
  return [0, 2, 4].map((i) => parseInt(full.slice(i, i + 2), 16));
};

const toHex = (rgb) =>
  `#${rgb
    .map((v) =>
      Math.max(0, Math.min(255, Math.round(v)))
        .toString(16)
        .padStart(2, "0")
    )
    .join("")}`;

const channel = (c) => {
  const s = c / 255;
  return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
};

const luminance = (hex) => {
  const [r, g, b] = parse(hex);
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
};

export const contrastRatio = (a, b) => {
  const [hi, lo] = [luminance(a), luminance(b)].sort((x, y) => y - x);
  return (hi + 0.05) / (lo + 0.05);
};

// Composite `fg` at opacity `a` over `bg` — the 8% tint used by active/selected states.
const composite = (fg, bg, a) => {
  const f = parse(fg);
  const b = parse(bg);
  return toHex(f.map((v, i) => v * a + b[i] * (1 - a)));
};

// Move toward black (amount < 0) or white (amount > 0), keeping the hue.
const shift = (hex, amount) => {
  const rgb = parse(hex);
  return amount < 0
    ? toHex(rgb.map((v) => v * (1 + amount)))
    : toHex(rgb.map((v) => v + (255 - v) * amount));
};

// Walk in 1% steps until `passes` is satisfied; give back the last step if it never is,
// so a pathological preset degrades to "as good as possible" instead of throwing.
const seek = (base, direction, passes) => {
  let last = base;
  for (let i = 0; i <= 100; i += 1) {
    last = shift(base, direction * (i / 100));
    if (passes(last)) return last;
  }
  return last;
};

const AA = 4.5;

/**
 * @param {string} base     preset accent hex (themePresets accentMain)
 * @param {string} baseFocus preset hover hex (themePresets accentFocus)
 * @param {string} surface  the surface these shades sit on ("#FFFFFF" / dark surface)
 * @param {"light"|"dark"} mode
 */
export default function accessibleAccent(base, baseFocus, surface, mode) {
  // Fills always carry white text, in both themes → darken until white clears AA.
  const fill = seek(base, -1, (c) => contrastRatio("#FFFFFF", c) >= AA);
  const fillFocus = seek(baseFocus, -1, (c) => contrastRatio("#FFFFFF", c) >= AA);

  // Text: darken on a light surface, lighten on a dark one. Must also clear the 8% tint
  // that active rows/chips paint under the label.
  const onSurface = seek(
    base,
    mode === "dark" ? 1 : -1,
    (c) => contrastRatio(c, surface) >= AA && contrastRatio(c, composite(c, surface, 0.08)) >= AA
  );

  return { fill, fillFocus, onSurface };
}
